import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useSearchResult } from '../context/SearchResultContext';
import { ButtonLink, Flex, Heading, SrOnly, Table, Text } from '@applyboard/crystal-ui';
import { Program } from '../data/usePrograms';
import { DocumentAlertHifiIcon, EyeOutlineIcon } from '@applyboard/ui-icons';

export function ProgramsTable() {
  const { apiError } = useErrorHandler();
  const { programs, isFetchingPrograms, isProgramsError } = useSearchResult();

  if (isProgramsError) {
    apiError();
    return null;
  }

  return (
    <Flex direction="column">
      <div role="alert" aria-live="assertive">
        {isFetchingPrograms && <SrOnly>Loading programs table</SrOnly>}
      </div>
      <Table
        data={programs}
        loading={isFetchingPrograms}
        overflow="scroll"
        columns={[
          {
            accessorKey: 'code',
            header: 'Program Code',
            cell: (program: Program) => <Table.TextCell>{program.code}</Table.TextCell>,
          },
          {
            accessorKey: 'name',
            header: 'Program Name',
            cell: (program: Program) => <Table.TextCell>{program.name}</Table.TextCell>,
          },
          {
            accessorKey: 'fieldOfStudyName',
            header: 'Field of Study',
            cell: (program: Program) => <Table.TextCell>{program.fieldOfStudyName}</Table.TextCell>,
          },
          {
            accessorKey: 'programLevelName',
            header: 'Program Level',
            cell: (program: Program) => <Table.TextCell>{program.programLevelName}</Table.TextCell>,
          },
          {
            accessorKey: 'link',
            header: () => <SrOnly>Program Link</SrOnly>,
            cell: (program: Program) => {
              return (
                <ButtonLink
                  aria-label={`Program ${program.name} Link`}
                  emphasis="transparent"
                  href={`/program-catalog/${program.id}`}
                  intent="secondary"
                  leadIcon={EyeOutlineIcon}
                  size="sm"
                  width="hug"
                />
              );
            },
          },
        ]}
      >
        <Table.NoData>
          <Flex align="center" justify="center" direction="column" gap={4} py={32}>
            <DocumentAlertHifiIcon />
            <Heading level={3}>No programs found!</Heading>
            <Text contrast="mid">No programs found at the moment.</Text>
          </Flex>
        </Table.NoData>
      </Table>
    </Flex>
  );
}
